// Provider Service - GraphQL operations for Providers
import { graphqlClient } from "../graphqlClient";

/**
 * Clean input data for GraphQL mutations
 * Removes __typename and other internal fields that the API doesn't accept
 */
const cleanInput = (data) => {
  if (!data || typeof data !== "object") return data;

  // Clone the object to avoid mutating original
  const cleaned = Array.isArray(data) ? [...data] : { ...data };

  if (Array.isArray(cleaned)) {
    return cleaned.map((item) => cleanInput(item));
  }

  // Fields to remove from all inputs
  const restrictedFields = ["__typename", "createdAt", "updatedAt"];

  Object.keys(cleaned).forEach((key) => {
    if (restrictedFields.includes(key)) {
      delete cleaned[key];
    } else if (typeof cleaned[key] === "object") {
      cleaned[key] = cleanInput(cleaned[key]);
    }
  });

  return cleaned;
};

/**
 * Pick only the fields accepted by CreateProviderInput / UpdateProviderInput
 * Backend schema: { code, name, nameEn, status }
 */
const PROVIDER_INPUT_FIELDS = ["code", "name", "status"];

const pickProviderInput = (data) => {
  const result = {};
  PROVIDER_INPUT_FIELDS.forEach((key) => {
    if (data[key] !== undefined) result[key] = data[key];
  });
  return result;
};

// ==================== Get All Providers ====================

export const getAllProviders = async () => {
  const query = `
    query GetAllProviders {
      getAllProviders {
            id
        code
        name

        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllProviders || [];
};

// ==================== Get Provider By ID ====================

export const getProviderById = async (id) => {
  const query = `
    query GetProviderById($id: String!) {
      getProviderById(id: $id) {
                id
        code
        name
  
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getProviderById;
};

// ==================== Create Provider ====================

export const createProvider = async (input) => {
  const mutation = `
    mutation CreateProvider($input: CreateProviderInput!) {
      createProvider(input: $input) {
        id
        code
        name
    
        status
        createdAt
        updatedAt
      }
    }
  `;

  const cleanedInput = cleanInput(pickProviderInput(input));
  const result = await graphqlClient.mutate(mutation, { input: cleanedInput });
  return result.createProvider;
};

// ==================== Update Provider ====================

export const updateProvider = async (id, input) => {
  // Pick only schema-accepted fields, then strip internal fields
  const cleanedInput = cleanInput(pickProviderInput(input));

  // Also remove 'id' from the input for updates as it's passed as a separate argument
  delete cleanedInput.id;

  const mutation = `
    mutation UpdateProvider($id: String!, $input: UpdateProviderInput!) {
      updateProvider(id: $id, input: $input) {
        id
        code
        name
    
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, {
    id,
    input: cleanedInput,
  });
  return result.updateProvider;
};

// ==================== Delete Provider ====================

export const deleteProvider = async (id) => {
  const mutation = `
    mutation DeleteProvider($id: String!) {
      deleteProvider(id: $id) {
        id
        name
        code
        status
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteProvider;
};
